import {io, Socket} from "socket.io-client";
import {API_URL} from "../../config";
import {store} from "../../store";
import {GameStore} from "../game/game-store";
import type {Ruleset} from "../../shared/rules";
import Match from "../../Match";

let socket: Socket | null = null;
// messages emitted while (re)connecting are buffered by socket.io and sent on connect

export const SocketState = {
    get socket() { return socket; },
    connect: () => {
        if (!API_URL) return; // no-backend mode
        if (socket) socket.disconnect();
        const token = store.getState().authentication.accessToken;
        GameStore.set({connection: "connecting"});
        socket = io(API_URL, {
            withCredentials: true,
            // evaluated on every (re)connect: tells the server which of our shots is still being played
            auth: (cb: (data: object) => void) => cb({...(token ? {token} : {}), ...(Match.activeShotId ? {shot: Match.activeShotId} : {})}),
            reconnectionDelay: 500,
            reconnectionDelayMax: 4000,
        });
        socket.on("connect", () => {
            GameStore.set({connection: "online"});
            Match.resync();
        });
        socket.on("disconnect", (reason) => {
            GameStore.set({connection: "connecting", searching: false});
            if (reason !== "io client disconnect") GameStore.toast("Connection lost, reconnecting…", "warn");
        });
        socket.on("connect_error", (err) => {
            GameStore.set({connection: "connecting"});
            if (err.message === "unauthorized") {
                GameStore.toast("Your session expired, please sign in again.", "warn", 8000);
                socket?.disconnect();
                GameStore.set({connection: "offline"});
            }
        });
        socket.on("match-found", (data) => Match.onMatchFound(data));
        socket.on("game:state", (data) => Match.onState(data));
        socket.on("game:notice", (data) => Match.onNotice(data));
        socket.on("game:shot-start", (data) => Match.onShotStart(data));
        socket.on("game:frames", (data) => Match.onFrames(data));
        socket.on("game:cue-move", (data) => Match.onCueMove(data));
    },
    disconnect: () => {
        if (!socket) return;
        socket.disconnect();
        socket = null;
        GameStore.set({connection: "offline", searching: false});
    },
    emit: (event: string, payload?: any, ack?: (r: any) => void) => {
        if (!socket) return;
        if (ack) socket.emit(event, payload, ack);
        else socket.emit(event, payload);
    },
    /** Queue for a match with the given ruleset (the server pairs only players who asked for the same one). */
    findMatch: (ruleset: Ruleset = "snooker") => new Promise<any>((resolve) => {
        if (!socket) return resolve({status: "offline"});
        Match.leaveFinishedGame();   // a new queue leaves the finished game on screen (same path as Back to menu)
        GameStore.set({searching: true});
        socket.emit("match:find", {ruleset}, (r: any) => {
            if (r?.status !== "queued") GameStore.set({searching: false});
            resolve(r);
        });
    }),
    cancelMatch: () => {
        GameStore.set({searching: false});
        socket?.emit("match:cancel");
    },
};
